import { ComponentProps, FC, useCallback, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Joyride, { CallBackProps, EVENTS } from 'react-joyride';
import TutorialTooltip from 'src/components/Creator/CreatorOnboarding/TutorialTooltip';
import useBeforeunload from 'src/hooks/useBeforeunload';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import useIsRtl from 'src/hooks/useIsRtl';
import useTheme from 'src/hooks/useTheme';
import { CreatorOnboardingStep, creatorOnboardingStepAtom } from 'src/stores/creator/atom';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { hexToRgb } from 'src/utils/common';
const CreatorTutorials: FC = () => {
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const setCreatorOnboardingStep = useSetAtom(creatorOnboardingStepAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const isDesktop = useCssMediaDevice() === 'desktop';
  const {
    t
  } = useTranslation();
  const isRtl = useIsRtl();
  const theme = useTheme();
  const spotlightClickHandlerRef = useRef<(() => void) | null>(null);
  const tutorialSteps = useMemo<ComponentProps<typeof Joyride>['steps']>(() => {
    return [{
      target: '#Creator-Sidebar',
      content: t('tutorial_ver2_onboarding1'),
      disableBeacon: true,
      spotlightPadding: 0,
      placement: isRtl ? 'left' : 'right',
      spotlightClicks: false,
      styles: {
        spotlight: {
          borderRadius: '20px',
          cursor: 'pointer'
        }
      },
      floaterProps: {
        styles: {
          floaterOpening: {
            left: isRtl ? '-20px' : '20px',
            top: '-240px'
          }
        }
      }
    }, {
      target: '#Creator-Sidebar-ViewModeOptions',
      content: t('tutorial_ver2_onboarding2'),
      disableBeacon: true,
      spotlightPadding: 12,
      spotlightClicks: true,
      placement: isRtl ? 'left' : 'right',
      data: {
        delay: 300
      },
      styles: {
        spotlight: {
          borderRadius: '20px'
        }
      },
      floaterProps: {
        styles: {
          floaterOpening: {
            left: isRtl ? '-20px' : '20px'
          }
        }
      }
    }, {
      target: '#Creator-Sidebar-CollapseButton',
      content: t('tutorial_ver2_onboarding3'),
      disableBeacon: true,
      spotlightPadding: 12,
      placement: isRtl ? 'left' : 'right',
      spotlightClicks: true,
      styles: {
        spotlight: {
          borderRadius: '24px'
        }
      },
      data: {
        delay: 300
      },
      floaterProps: {
        styles: {
          floaterOpening: {
            left: isRtl ? '-20px' : '20px'
          }
        }
      }
    }, {
      target: '#Creator-Toolbar-Match-Filters',
      content: t('tutorial_ver2_onboarding4'),
      disableBeacon: true,
      placement: 'top',
      /**
       * 버튼 클릭을 허용하는 경우 필터 모달이 켜져서 튜토리얼 플로우가 지나치게 길어질 수 있음
       * (기획적 요구사항으로 방지)
       */
      spotlightClicks: false,
      spotlightPadding: 10,
      floaterProps: {
        styles: {
          floaterOpening: {
            top: '-26px'
          }
        }
      },
      styles: {
        spotlight: {
          borderRadius: '20px',
          cursor: 'pointer'
        }
      }
    }, {
      target: '#Creator-Toolbar-Prime-Button',
      content: t('tutorial_ver2_onboarding5'),
      disableBeacon: true,
      placement: 'top',
      spotlightClicks: true,
      spotlightPadding: 10,
      floaterProps: {
        styles: {
          floaterOpening: {
            top: '-26px'
          }
        }
      },
      styles: {
        spotlight: {
          borderRadius: '20px'
        }
      }
    }];
  }, [t, isRtl]);
  const onBeforeUnload = useCallback(() => {
    // 튜토리얼은 중간에 이탈해도 한 번 노출되었으면 재노출 X
    setCreatorOnboardingStep(CreatorOnboardingStep.Done);
  }, [setCreatorOnboardingStep]);
  useBeforeunload(onBeforeUnload);

  /**
   * 요소가 마운트될 때까지 기다리는 함수
   */
  const waitForElement = useCallback((selector: string): Promise<Element | null> => {
    return new Promise(resolve => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }
      const TIMEOUT_MS = 5000;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          resolve(el);
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // 타임아웃 설정
      timeoutId = setTimeout(() => {
        Sentry.captureMessage(`Timeout because element didn't mount`, {
          extra: {
            selector
          }
        });
        observer.disconnect();
        resolve(null);
      }, TIMEOUT_MS);
    });
  }, []);
  const removeStepEventListener = useCallback((step: ComponentProps<typeof Joyride>['steps'][number]) => {
    if (!spotlightClickHandlerRef.current || typeof step.target !== 'string') {
      return;
    }
    if (step.spotlightClicks) {
      // spotlightClicks: true인 경우, targetElement에서 제거
      const targetElement = document.querySelector(step.target);
      targetElement?.removeEventListener('click', spotlightClickHandlerRef.current);
    } else {
      // spotlightClicks: false인 경우, spotlightElement에서 제거
      const spotlightElement = document.querySelector('.react-joyride__spotlight');
      spotlightElement?.removeEventListener('click', spotlightClickHandlerRef.current);
    }
  }, []);
  const goToNextStep = useCallback(async (currentIndex: number, delay = 0) => {
    // 마지막 스텝인 경우: 이벤트 리스너 제거 후 종료 처리
    if (currentIndex === tutorialSteps.length - 1) {
      const currentStep = tutorialSteps[currentIndex];
      removeStepEventListener(currentStep);
      setCreatorOnboardingStep(CreatorOnboardingStep.Done);
      closeModal(ModalType.CREATOR_ONBOARDING);
      return;
    }
    const nextStepIndex = currentIndex + 1;
    const nextStep = tutorialSteps[nextStepIndex];
    if (typeof nextStep.target === 'string') {
      await waitForElement(nextStep.target);
    }
    setTimeout(() => {
      setTutorialStepIndex(nextStepIndex);
    }, delay);
  }, [tutorialSteps, removeStepEventListener, setCreatorOnboardingStep, closeModal, waitForElement, setTutorialStepIndex]);
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const {
      index,
      type,
      step
    } = data;
    if (type === EVENTS.TOOLTIP) {
      const spotlightElement = document.querySelector('.react-joyride__spotlight');
      if (spotlightClickHandlerRef.current) {
        const prevStep = tutorialSteps[index - 1];
        removeStepEventListener(prevStep);
      }
      if (spotlightElement) {
        const handleSpotlightClick = () => {
          goToNextStep(index, step.data?.delay ?? 0);
        };
        if (step.spotlightClicks) {
          // 직접 타겟을 클릭하는 경우 타겟에 이벤트 리스너 추가
          const targetElement = document.querySelector(step.target as string);
          if (targetElement) {
            spotlightClickHandlerRef.current = handleSpotlightClick;
            targetElement.addEventListener('click', handleSpotlightClick);
          }
          return;
        }
        spotlightClickHandlerRef.current = handleSpotlightClick;
        spotlightElement.addEventListener('click', handleSpotlightClick);
      }
    }
  }, [goToNextStep, tutorialSteps, removeStepEventListener]);

  // 크리에이터 기능은 데스크탑 기기 및 레이아웃에서만 제공
  if (!isDesktop) return null;
  return <Joyride steps={tutorialSteps} stepIndex={tutorialStepIndex} callback={handleJoyrideCallback} disableScrolling spotlightClicks disableOverlayClose tooltipComponent={TutorialTooltip} styles={{
    overlay: {
      backgroundColor: `rgba(${hexToRgb(theme.color.surfaceBright)}, 0.7)`
    },
    options: {
      arrowColor: 'transparent' // 라이브러리에서 기본 제공하는 툴팁 화살표 숨김 처리
    }
  }} data-sentry-element="Joyride" data-sentry-component="CreatorTutorials" data-sentry-source-file="Tutorials.tsx" />;
};
export default CreatorTutorials;